import {create} from 'zustand';
import {Store, StoreStatistics} from '../models/store.model';
import {Pagination} from '../../../Data/types/BaseResponse';
import {storeFactory} from './storeFactory';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {StoreListFilters} from '../repository/StoreRepository';

interface StoreState {
  // Data
  stores: Store[];
  statistics: StoreStatistics | null;
  pagination: Pagination | undefined;

  // Loading states
  loading: boolean;
  statisticsLoading: boolean;

  // Error states
  error: string | null;
  statisticsError: string | null;

  // Actions
  fetchStores: (page?: number, limit?: number, filters?: StoreListFilters) => Promise<void>;
  fetchStatistics: () => Promise<void>;
  createStore: (store: Store) => Promise<Store>;
  updateStore: (id: string, store: Store) => Promise<Store>;
  deleteStore: (id: string) => Promise<boolean>;
  setPagination: (pagination: Pagination | undefined) => void;
  refreshData: () => Promise<void>;
  clearErrors: () => void;
}

export const useStoreStore = create<StoreState>((set, get) => {
  // Initialize use cases using factory
  const {getAllStores, getStatistics, createStore: createStoreUseCase, updateStore: updateStoreUseCase, deleteStore: deleteStoreUseCase} = storeFactory.getUseCases();
  const errorHandlingService = StandardizedErrorHandler.getInstance();

  return {
    // Initial state
    stores: [],
    statistics: null,
    pagination: undefined,
    loading: false,
    statisticsLoading: false,
    error: null,
    statisticsError: null,

    // Actions
    fetchStores: async (page = 1, limit = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE, filters?: StoreListFilters) => {
      set({loading: true, error: null});
      try {
        const {items, pagination} = await getAllStores.execute(page, limit, filters);

        set({stores: items, pagination, loading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({error: appError.message, loading: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    fetchStatistics: async () => {
      set({statisticsLoading: true, statisticsError: null});
      try {
        const stats = await getStatistics.execute();
        set({statistics: stats, statisticsLoading: false});
      } catch (err) {
        const appError = errorHandlingService.toAppError(err);
        set({statisticsError: appError.message, statisticsLoading: false});
        errorHandlingService.handleError(err); // For notifications/logging
      }
    },

    createStore: async (store: Store) => {
      try {
        const newStore = await createStoreUseCase.execute(store);
        // Refresh data after creation
        await get().refreshData();
        return newStore;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    updateStore: async (id: string, store: Store) => {
      try {
        const updatedStore = await updateStoreUseCase.execute(id, store);
        // Refresh data after update
        await get().refreshData();
        return updatedStore;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    deleteStore: async (id: string): Promise<boolean> => {
      try {
        await deleteStoreUseCase.execute(id);
        // Refresh data after deletion
        await get().refreshData();
        return true;
      } catch (err) {
        // Don't handle error here - just throw it so it can be handled at the component level
        // This prevents duplicate error notifications
        throw err;
      }
    },

    setPagination: (pagination) => {
      set({pagination});
    },

    refreshData: async () => {
      const currentState = get();
      const page = currentState.pagination?.page || PAGINATION_CONFIG.DEFAULT_PAGE;
      const limit = currentState.pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;

      await Promise.all([
        get().fetchStores(page, limit),
        get().fetchStatistics()
      ]);
    },

    clearErrors: () => {
      set({error: null, statisticsError: null});
    }
  };
});
